"use client";
'use strict';

var React = require('react');
var core = require('@popperjs/core');
var _function = require('../utils/function.js');
var refs = require('../utils/refs.js');

const transforms = {
    top: 'bottom center',
    'top-start': 'bottom left',
    'top-end': 'bottom right',
    bottom: 'top center',
    'bottom-start': 'top left',
    'bottom-end': 'top right',
    left: 'right center',
    // "left-start": "right top",
    // "left-end": "right bottom",
    right: 'left center',
    // "right-start": "left top",
    // "right-end": "left bottom",
};
// @ts-ignore
const toTransformOrigin = (placement) => transforms[placement];
const defaultEventListeners = {
    scroll: true,
    resize: true,
};
function getEventListenerOptions(value) {
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let eventListeners;
    if (typeof value === 'object') {
        eventListeners = {
            enabled: true,
            options: { ...defaultEventListeners, ...value },
        };
    }
    else {
        eventListeners = {
            enabled: value,
            options: defaultEventListeners,
        };
    }
    return eventListeners;
}
const matchWidthModifier = {
    name: 'matchWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        state.styles.popper.width = `${state.rects.reference.width}px`;
    },
    effect: ({ state }) => () => {
        const reference = state.elements.reference;
        state.elements.popper.style.width = `${reference.offsetWidth}px`;
    },
};
const matchMinWidthModifier = {
    name: 'matchMinWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        state.styles.popper.minWidth = `${state.rects.reference.width}px`;
    },
    effect: ({ state }) => () => {
        const reference = state.elements.reference;
        state.elements.popper.style.minWidth = `${reference.offsetWidth}px`;
    },
};
function usePositioner(props = {}) {
    const { enabled = true, modifiers = [], placement: placementProp = 'top', strategy = 'absolute', arrowPadding = 8, 
    // eventListeners = true,
    offset, gutter = 8, flip = true, boundary = 'clippingParents', preventOverflow = true, matchWidth = false, } = props;
    const reference = React.useRef(null);
    const popper = React.useRef(null);
    const arrow = React.useRef(null);
    const instance = React.useRef(null);
    const cleanup = React.useRef(_function.noop);
    const setupPopper = React.useCallback(() => {
        if (!enabled || !reference.current || !popper.current)
            return;
        // If popper instance exists, destroy it so we can create a new one
        cleanup.current?.();
        instance.current = core.createPopper(reference.current, popper.current, {
            placement: placementProp,
            modifiers: [
                { ...matchWidthModifier, enabled: !!matchWidth },
                { ...matchMinWidthModifier, enabled: !matchWidth },
                // {
                //   name: "eventListeners",
                //   ...getEventListenerOptions(eventListeners),
                // },
                {
                    name: 'arrow',
                    options: {
                        element: arrow.current,
                        padding: arrowPadding,
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset: offset || [0, gutter],
                    },
                },
                {
                    name: 'flip',
                    enabled: !!flip,
                    options: { padding: 8 },
                },
                {
                    name: 'preventOverflow',
                    enabled: !!preventOverflow,
                    options: { boundary },
                },
                // allow users override internal modifiers
                ...modifiers,
            ],
            strategy,
        });
        // force update one-time to fix any positioning issues
        instance.current.forceUpdate();
        cleanup.current = instance.current.destroy;
    }, [
        enabled,
        placementProp,
        modifiers,
        matchWidth,
        arrowPadding,
        offset,
        gutter,
        flip,
        preventOverflow,
        boundary,
        strategy,
    ]);
    React.useEffect(() => () => {
        /**
         * Fast refresh might call this function and tear down the popper
         * even if the reference still exists. This checks against that
         */
        if (!reference.current && !popper.current) {
            instance.current?.destroy();
            instance.current = null;
        }
    }, []);
    const referenceRef = React.useCallback((node) => {
        reference.current = node;
        setupPopper();
    }, [setupPopper]);
    const getReferenceProps = React.useCallback((props = {}, ref = null) => ({
        ...props,
        ref: refs.mergeRefs(referenceRef, ref),
    }), [referenceRef]);
    const popperRef = React.useCallback((node) => {
        popper.current = node;
        setupPopper();
    }, [setupPopper]);
    const getPopperProps = React.useCallback((props = {}, ref = null) => ({
        ...props,
        ref: refs.mergeRefs(popperRef, ref),
        style: {
            zIndex: 1000,
            ...props.style,
            position: strategy,
            inset: '0 auto auto 0',
        },
    }), [strategy, popperRef]);
    const arrowRef = React.useCallback((node) => {
        arrow.current = node;
        setupPopper();
    }, [setupPopper]);
    const getArrowProps = React.useCallback((props = {}, ref = null) => {
        const { size, shadowColor, bg, style, ...rest } = props;
        return {
            ...rest,
            ref,
            'data-popper-arrow': '',
        };
    }, []);
    return {
        update: instance.current?.update,
        forceUpdate: instance.current?.forceUpdate,
        referenceRef,
        popperRef,
        arrowRef,
        getPopperProps,
        getArrowProps,
        getReferenceProps,
    };
}

exports.getEventListenerOptions = getEventListenerOptions;
exports.matchMinWidthModifier = matchMinWidthModifier;
exports.matchWidthModifier = matchWidthModifier;
exports.toTransformOrigin = toTransformOrigin;
exports.usePositioner = usePositioner;
//# sourceMappingURL=usePositioner.js.map
